// @ts-nocheck
import moment from 'moment-timezone';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { ButtonUI, DateRangePicker, Prompt, ReportFilters, Selector } from 'sonata-react-components';

import { timeRangeOptionIds } from '@/modules/reporting/ReportingSectionAdvancedFilter/helpers';
import { useReportingVariableFilters } from '@/ts-modules/reporting/hooks/useReportingVariableFilters';
import { DATE, useTimeFrameOptions } from '@/ts-modules/reporting/hooks/useTimeFrameOptions';
import { useReportDownloadSectionContext } from '../../ReportDownloadSection.context';
import CampaignsSettings from '../CampaignsSettings/CampaignsSettings';
import { FiltersSettingsStyled } from './FiltersSettings.style';

import type { Moment } from 'moment-timezone';
import type { SelectorOptionPropsInterface } from 'sonata-react-components';
import type { DataByType } from '../../types';

type State = {
  customDateValues: {
    endDate: Moment;
    startDate: Moment;
  };
  withCustomDates: boolean;
};

type Props = {
  tabId: string;
};

function FiltersSettings({ tabId }: Props) {
  const dateFormat = taptap.app.i18n_dates.getDateLocalizations().shortDate;
  const { formatMessage } = useIntl();
  const [showPrompt, setShowPrompt] = useState(false);
  const [dataType, setDataType] = useState<keyof DataByType>('stats');
  const {
    getTabOptions,
    hasAttribution,
    initialDateRange,
    isMultiCampaigns,
    setColumnsByType,
    setDates,
    setDecimalsType,
    setFilters,
    setGroupsFromReporting,
    setTimeFrame,
    type
  } = useReportDownloadSectionContext();

  const activeTab = getTabOptions(tabId);

  const [{ customDateValues, withCustomDates }, setState] = useState<State>({
    customDateValues: initialDateRange,
    withCustomDates: activeTab.timeFrame === 'customDates'
  });

  const { options: filterOptions } = useReportingVariableFilters({ type: activeTab.sheetType });
  const timeOptions = useTimeFrameOptions();

  const separators: SelectorOptionPropsInterface[] = [
    {
      id: 'comma',
      label: formatMessage({
        defaultMessage: 'Comma - Ex: 1.991,16',
        id: 'reporting.reportingDownload.options.commaSeparator'
      })
    },
    {
      id: 'dot',
      label: formatMessage({
        defaultMessage: 'Dot - Ex: 1,991.16',
        id: 'reporting.reportingDownload.options.dotSeparator'
      })
    }
  ];

  const dateRangeOptions = timeRangeOptionIds.map(optionId => {
    return {
      id: optionId,
      label: formatMessage({
        defaultMessage: optionId,
        id: `reporting.advancedFilters.date.options.${optionId}`
      })
    };
  });

  const defaultSelectedOptions = activeTab.options.groups
    .filter(groupId => [...timeOptions, ...filterOptions].some(option => groupId === option.id))
    .map(groupId => {
      const timeOption = timeOptions.find(option => groupId === option.id);
      if (timeOption) {
        return timeOption;
      }
      return filterOptions.find(option => groupId === option.id)!;
    });

  const onChangeFilters = (filters: string[]) => {
    setFilters(
      filters.filter(value => !DATE.includes(value)),
      filters,
      tabId
    );
  };

  const onChangeSeparator = (separator: SelectorOptionPropsInterface) => setDecimalsType(separator.id as string, tabId);

  const onChangeDateRange = (option: SelectorOptionPropsInterface) => {
    setTimeFrame(option.id as string, tabId);
    setState(prev => ({
      ...prev,
      withCustomDates: option.id === 'customDates'
    }));
  };

  const onChangeDates = (newValues: any) => {
    setState(prev => ({
      ...prev,
      customDateValues: newValues
    }));
    if (newValues.startDate && newValues.endDate) {
      setDates(tabId, newValues.startDate.toDate(), newValues.endDate.toDate());
    }
  };

  const minDate = hasAttribution
    ? moment(initialDateRange.startDate).subtract(15, 'days').toDate()
    : customDateValues.startDate;
  const maxDate = hasAttribution ? moment(initialDateRange.endDate).add(16, 'days').toDate() : customDateValues.endDate;

  const isOutsideRange = (date: Moment) => date.endOf('day').isBefore(minDate) || date.endOf('day').isAfter(maxDate);

  const handleDataTypeChange = ({ value }: SelectorOptionPropsInterface) => {
    if (value) {
      setColumnsByType(tabId, value as keyof DataByType);
      setDataType(value as keyof DataByType);
    }
  };

  const dataTypeOptions = [
    {
      id: 'stats',
      label: formatMessage({
        defaultMessage: 'Stats',
        id: 'reporting.reportingDownload.filters.dataType.stats'
      })
    }
  ];

  if (activeTab.hasFootfallAttribution) {
    dataTypeOptions.push({
      id: 'footfallAttribution',
      label: formatMessage({
        defaultMessage: 'Footfall Attribution',
        id: 'reporting.reportingDownload.filters.dataType.footfallAttribution'
      })
    });
  } else if (activeTab.hasViewability) {
    dataTypeOptions.push({
      id: 'viewability',
      label: formatMessage({
        defaultMessage: 'Viewability',
        id: 'reporting.reportingDownload.filters.dataType.viewability'
      })
    });
  }

  return (
    <FiltersSettingsStyled className="filtersSettings">
      <div className="filtersSettings__datesSection">
        <Selector
          defaultValue={[
            separators.find(item => item.id === (activeTab.options.decimalsType === 'point' ? 'dot' : 'comma'))!
          ]}
          id="separators"
          isDisabled={type === 'excel'}
          label={formatMessage({
            defaultMessage: 'Numeric Separator Format',
            id: 'reporting.reportingDownload.options.separatorLabel'
          })}
          onChange={onChangeSeparator}
          options={separators}
          type="simple"
        />
        {dataTypeOptions.length > 1 ? (
          <Selector
            defaultValue={[dataTypeOptions[0]]}
            label={formatMessage({
              defaultMessage: 'Data Type',
              id: 'reporting.reportingDownload.filters.dataType.label'
            })}
            onChange={handleDataTypeChange}
            options={dataTypeOptions}
            type="simple"
          />
        ) : null}
        <Selector
          defaultValue={[dateRangeOptions.find(item => item.id === activeTab.timeFrame)!]}
          id="timeRange"
          label={formatMessage({
            defaultMessage: 'Date Range',
            id: 'reporting.reportingDownload.options.dateRange'
          })}
          onChange={onChangeDateRange}
          options={dateRangeOptions}
          type="simple"
        />
        {withCustomDates && (
          <DateRangePicker
            displayFormat={dateFormat}
            endDate={customDateValues.endDate}
            endDateId={`${tabId}_endDate`}
            isOutsideRange={isOutsideRange}
            maxDate={maxDate}
            minDate={minDate}
            minimumNights={0}
            onDatesChange={onChangeDates}
            required
            showClearDates={false}
            startDate={customDateValues.startDate}
            startDateId={`${tabId}_beginDate`}
          />
        )}
      </div>
      {isMultiCampaigns && (
        <div className="filtersSettings__section">
          <CampaignsSettings tabId={tabId} />
        </div>
      )}
      <div className="filtersSettings__section">
        <div>
          {formatMessage({
            defaultMessage: 'Set filters',
            id: 'reporting.reportingDownload.filters.title'
          })}
        </div>
        <div className="filtersSettings__sectionContent">
          <ButtonUI
            className="filtersSettings__sectionContentImportButton"
            icon="fa-regular fa-arrow-up-from-dotted-line"
            onClick={() => setShowPrompt(true)}
            shape="link"
            text={formatMessage({
              defaultMessage: 'Import Filters From Report',
              id: 'reporting.reportingDownload.filters.import'
            })}
            type="secondary"
          />
          <ReportFilters
            defaultSelectedOptions={defaultSelectedOptions || []}
            groupOptions={filterOptions}
            key={`${dataType}_${defaultSelectedOptions.map(d => d.id).join('_')}`}
            numberOfSelectableOptions={5}
            onChange={onChangeFilters}
            timeOptions={timeOptions}
          />
        </div>
      </div>
      {showPrompt && (
        <Prompt
          buttons={[
            {
              onClick: () => {
                setGroupsFromReporting(tabId, dataType);
                setShowPrompt(false);
              },
              text: formatMessage({
                defaultMessage: 'Confirm',
                id: 'common.confirm'
              }),
              type: 'primary'
            }
          ]}
          closePrompt={() => setShowPrompt(false)}
          description={formatMessage({
            defaultMessage: 'Importing new filters will overwrite your current configuration.',
            id: 'reporting.reportingDownload.prompt.description'
          })}
          icon="fas fa-exclamation-triangle"
          title={formatMessage({
            defaultMessage: 'Are you sure you want to import files?',
            id: 'reporting.reportingDownload.prompt.title'
          })}
        />
      )}
    </FiltersSettingsStyled>
  );
}

export default FiltersSettings;
